import {
  createAction,
  createFeatureSelector,
  createReducer,
  createSelector,
  on,
  props,
} from '@ngrx/store';
import { Injectable } from '@angular/core';
import { createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, withLatestFrom } from 'rxjs';

import { communitySelectors } from '@redux/community.effects';
import { Weather } from '@models/common/weather.model';
import { BaseEffects } from '@redux/common/base.effects';

export interface CrosscuttingState {
  weather: Weather | null;
}

export const initialCrosscuttingState: CrosscuttingState = {
  weather: null,
};

export const crosscuttingStateKey = 'CrosscuttingState';

export const crosscuttingActions = {
  weather: createAction(`[${crosscuttingStateKey}]Weather`),
  weatherSuccess: createAction(
    `[${crosscuttingStateKey}]WeatherSuccess`,
    props<{ weather: Weather }>(),
  ),
  weatherFail: createAction(`[${crosscuttingStateKey}]WeatherFail`, props<{ error: string }>()),
  //
};

const selectCrosscuttingFeature = createFeatureSelector<CrosscuttingState>(crosscuttingStateKey);

export const selectCrosscutting = {
  selectWeather: createSelector(
    selectCrosscuttingFeature,
    (state: CrosscuttingState) => state.weather,
  ),
  selectWeatherLoaded: createSelector(
    selectCrosscuttingFeature,
    (state: CrosscuttingState) => !!state.weather,
  ),
};

export const crosscuttingReducer = createReducer(
  initialCrosscuttingState,

  on(
    crosscuttingActions.weatherSuccess,
    (state, { weather }): CrosscuttingState => ({
      ...state,
      weather,
    }),
  ),
);

@Injectable()
export class CrosscuttingEffects extends BaseEffects {
  weather$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(crosscuttingActions.weather),
      withLatestFrom(
        this.store.select(selectCrosscutting.selectWeatherLoaded),
        this.store.select(communitySelectors.selectModel),
      ),
      mergeMap(([, loaded, community]) => {
        if (!loaded && community) {
          return this.api.get<Weather>(`communities/${community.id}/weather`).pipe(
            map((response) => {
              if (response) {
                return crosscuttingActions.weatherSuccess({
                  weather: response,
                });
              }
              const { message } = response;
              return crosscuttingActions.weatherFail({
                error: message || 'An error occurred.',
              });
            }),
            catchError((err) => {
              return of(
                crosscuttingActions.weatherFail({
                  error: err?.error?.message || err?.message || 'An error occurred.',
                }),
              );
            }),
          );
        }
        return of();
      }),
    );
  });
}
